/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { BranchTypesDaoService } from '../branch-types-dao.service';
import {
  DbPersistenceService,
  Query
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { AppConfig } from 'src/app/app.config';
import { BranchTyp } from 'src/app/_model/branch-types';
import { BranchTypesDbTableDefinition } from './branch-types-db-table-definition';
import { LocalBranchTypesDbTableDefinition } from './local-branch-types-db-table-definition';


@Injectable()
export class BranchTypesDbDaoService implements BranchTypesDaoService {
  
  // Lifecycle
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private branchTypesTableDefinition: BranchTypesDbTableDefinition,
    private localBranchTypesTableDefinition: LocalBranchTypesDbTableDefinition,
  ) { }

  public async saveBranchTypes(branchTypes: BranchTyp[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      var branchTypQueries: Query[] = [];
      for (const branchTyp of branchTypes) {
        let branchTypQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            (branchTyp.isLocal() ? this.localBranchTypesTableDefinition.tableName : this.branchTypesTableDefinition.tableName) +
            '(id, databag, name, shortname)' +
            ' VALUES (?,?,?,?)',
          parameters: [
            Number(branchTyp.getImsId()),
            branchTyp.databag ? JSON.stringify(branchTyp.databag) : undefined,
            branchTyp.label,
            branchTyp.shortname ? branchTyp.shortname : ''
          ]
        };
        branchTypQueries.push(branchTypQuery);
      }

      await this.dbPersistenceService.executeBatch(db, branchTypQueries);
    } catch (error) {
      this.logger.log(
        'Error saving branchTypes to db',
        error,
        '-',
        error?.message
      );
    }
  }
  public async getBranchTypes(): Promise<BranchTyp[]> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let branchTypes: BranchTyp[] = [];

      // Get Synced Branches
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.branchTypesTableDefinition.tableName + ';',
        parameters: null
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        branchTypes.push(
          BranchTyp.fromDto({
            id: row['id'],
            secondaryid: undefined,
            name: row['name'],
            shortname: row['shortname']
          }, row['databag'] ? JSON.parse(row['databag']): undefined)
        );
      }

      // Get local generated branchTypes
      let localData = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.localBranchTypesTableDefinition.tableName + ';',
        parameters: null
      });
      for (let i = 0; i < localData.values.length; i++) {
        branchTypes.push(BranchTyp.fromDto(localData.values[i]));
      }

      return branchTypes;
    } catch (error) {
      this.logger.log('Error reading branchTyp from db', error);
      return undefined
    }
  }

  public async reset() {
    // Do nothing
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(this.constructor.name, "truncate table", this.branchTypesTableDefinition.tableName)
    await this.dbPersistenceService.truncateTable(db, this.branchTypesTableDefinition.tableName);
    this.logger.log(this.constructor.name, "truncate table", this.localBranchTypesTableDefinition.tableName)
    await this.dbPersistenceService.truncateTable(db, this.localBranchTypesTableDefinition.tableName);
  }

}
